import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { useAuth } from './AuthContext'
import type { Family, Child, ChildInsert } from '@/types/database'
import type { UserEntitlementOverride, EffectiveEntitlements } from '@/types/admin'
import { getEffectiveEntitlements } from '@/services/entitlementsService'

interface FamilyContextValue {
  family: Family | null
  children: Child[]
  activeWriter: Child | null
  isLoading: boolean
  error: string | null
  userOverride: UserEntitlementOverride | null
  effectiveEntitlements: EffectiveEntitlements
  isBlocked: boolean
  /** True when user is authenticated but has no family record yet */
  needsFamilySetup: boolean
  setActiveWriter: (child: Child | null) => void
  addChild: (child: Omit<ChildInsert, 'family_id'>) => Promise<Child | null>
  updateChild: (id: string, updates: Partial<Child>) => Promise<void>
  refreshFamily: () => Promise<void>
  /** Create family record if one doesn't exist (edge case recovery) */
  createFamily: (parentName: string) => Promise<boolean>
}

const FamilyContext = createContext<FamilyContextValue | undefined>(undefined)

// Key for storing active writer in localStorage
const ACTIVE_WRITER_KEY = 'inkwell_jr_active_writer'

export function FamilyProvider({ children: reactChildren }: { children: ReactNode }) {
  const { user } = useAuth()
  const [family, setFamily] = useState<Family | null>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [activeWriter, setActiveWriterState] = useState<Child | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userOverride, setUserOverride] = useState<UserEntitlementOverride | null>(null)

  // Compute effective entitlements from family + override
  const effectiveEntitlements = getEffectiveEntitlements(family, userOverride)
  const isBlocked = effectiveEntitlements.blocked

  // Detect when user needs to complete family setup (authenticated but no family record)
  const needsFamilySetup = !isLoading && !!user && !family

  // Load family and children when user changes
  useEffect(() => {
    if (!user) {
      setFamily(null)
      setChildren([])
      setActiveWriterState(null)
      setUserOverride(null)
      setIsLoading(false)
      return
    }

    loadFamily()
  }, [user])

  // Restore active writer from localStorage
  useEffect(() => {
    if (children.length > 0) {
      const savedWriterId = localStorage.getItem(ACTIVE_WRITER_KEY)
      if (savedWriterId) {
        const savedWriter = children.find((c) => c.id === savedWriterId)
        if (savedWriter) {
          setActiveWriterState(savedWriter)
        }
      }
    }
  }, [children])

  const loadFamily = useCallback(async () => {
    if (!isSupabaseConfigured() || !user) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Fetch family and user override in parallel
      const [familyResult, overrideResult] = await Promise.all([
        supabase
          .from('families')
          .select('*')
          .eq('auth_id', user.id)
          .single(),
        supabase
          .from('user_entitlement_overrides')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
      ])

      // Handle override (may not exist)
      if (!overrideResult.error) {
        setUserOverride(overrideResult.data as UserEntitlementOverride | null)
      }

      // Handle family
      if (familyResult.error) {
        // Family might not exist yet (new user)
        if (familyResult.error.code === 'PGRST116') {
          setFamily(null)
          setChildren([])
        } else {
          throw familyResult.error
        }
      } else {
        setFamily(familyResult.data as Family)

        // Get children for this family
        const { data: childrenData, error: childrenError } = await supabase
          .from('children')
          .select('*')
          .eq('family_id', (familyResult.data as Family).id)
          .order('created_at', { ascending: true })

        if (childrenError) throw childrenError
        setChildren((childrenData as Child[]) || [])
      }
    } catch (err) {
      console.error('Error loading family:', err)
      setError(err instanceof Error ? err.message : 'Failed to load family data')
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const setActiveWriter = useCallback((child: Child | null) => {
    setActiveWriterState(child)
    if (child) {
      localStorage.setItem(ACTIVE_WRITER_KEY, child.id)
    } else {
      localStorage.removeItem(ACTIVE_WRITER_KEY)
    }
  }, [])

  const addChild = useCallback(
    async (childData: Omit<ChildInsert, 'family_id'>): Promise<Child | null> => {
      if (!family || !isSupabaseConfigured()) return null

      try {
        const { data, error } = await supabase
          .from('children')
          .insert({
            ...childData,
            family_id: family.id,
          } as Record<string, unknown>)
          .select()
          .single()

        if (error) throw error

        const newChild = data as Child
        setChildren((prev) => [...prev, newChild])
        return newChild
      } catch (err) {
        console.error('Error adding child:', err)
        setError(err instanceof Error ? err.message : 'Failed to add child')
        return null
      }
    },
    [family]
  )

  const updateChild = useCallback(
    async (id: string, updates: Partial<Child>) => {
      if (!isSupabaseConfigured()) return

      try {
        const { error } = await supabase
          .from('children')
          .update(updates as Record<string, unknown>)
          .eq('id', id)

        if (error) throw error

        setChildren((prev) =>
          prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
        )

        // Update active writer if it's the one being updated
        if (activeWriter?.id === id) {
          setActiveWriterState((prev) => (prev ? { ...prev, ...updates } : null))
        }
      } catch (err) {
        console.error('Error updating child:', err)
        setError(err instanceof Error ? err.message : 'Failed to update child')
      }
    },
    [activeWriter]
  )

  // Create family record (edge case recovery when trigger didn't fire)
  const createFamily = useCallback(
    async (parentName: string): Promise<boolean> => {
      if (!isSupabaseConfigured() || !user) return false
      if (family) return true // Already has family

      try {
        const { data, error } = await supabase
          .from('families')
          .insert({
            auth_id: user.id,
            parent_email: user.email!,
            parent_name: parentName,
          })
          .select()
          .single()

        if (error) throw error

        setFamily(data as Family)
        return true
      } catch (err) {
        console.error('Error creating family:', err)
        setError(err instanceof Error ? err.message : 'Failed to create family')
        return false
      }
    },
    [user, family]
  )

  return (
    <FamilyContext.Provider
      value={{
        family,
        children,
        activeWriter,
        isLoading,
        error,
        userOverride,
        effectiveEntitlements,
        isBlocked,
        needsFamilySetup,
        setActiveWriter,
        addChild,
        updateChild,
        refreshFamily: loadFamily,
        createFamily,
      }}
    >
      {reactChildren}
    </FamilyContext.Provider>
  )
}

export function useFamily() {
  const context = useContext(FamilyContext)
  if (context === undefined) {
    throw new Error('useFamily must be used within a FamilyProvider')
  }
  return context
}

// Convenience hook for just the active writer
export function useWriter() {
  const { activeWriter, setActiveWriter } = useFamily()
  return { writer: activeWriter, setWriter: setActiveWriter }
}
